import { 
  HAProxyInstance, 
  HAProxyBackend, 
  HAProxyServer, 
  DomainMapping,
  HAProxyTemplate,
  NFSServer,
  NFSShare
} from '../types';

// Generic fetch handler with error handling
export async function fetchApi<T = any>(
  url: string,
  options?: RequestInit,
  suppressLogForStatus?: number[]
): Promise<T> {
  const base = import.meta.env.PROD ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api');
  const res = await fetch(base + url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    },
  });
  if (!res.ok) {
    const errorText = await res.text();
    if (!suppressLogForStatus || !suppressLogForStatus.includes(res.status)) {
      console.error(`API request failed for ${url}: ${errorText}`);
    }
    throw new Error(errorText || `Request failed with status ${res.status}`);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return res.json();
}

// Instances API
export const instancesApi = {
  getAll: (): Promise<HAProxyInstance[]> => 
    fetchApi<HAProxyInstance[]>('/instances'),
  
  getById: (id: string): Promise<HAProxyInstance> => 
    fetchApi<HAProxyInstance>(`/instances/${id}`),
  
  create: (instance: Omit<HAProxyInstance, 'id' | 'status' | 'lastChecked'>): Promise<HAProxyInstance> => 
    fetchApi<HAProxyInstance>('/instances', { 
      method: 'POST', 
      body: JSON.stringify(instance) 
    }),
  
  update: (id: string, instance: Omit<HAProxyInstance, 'id' | 'status' | 'lastChecked'>): Promise<HAProxyInstance> => 
    fetchApi<HAProxyInstance>(`/instances/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(instance) 
    }),
  
  delete: (id: string): Promise<void> => 
    fetchApi<void>(`/instances/${id}`, { method: 'DELETE' }),

  getStatus: (id: string): Promise<any> => 
    fetchApi<any>(`/instances/${id}/status`),

  reload: (id: string): Promise<any> => 
    fetchApi<any>(`/instances/${id}/reload`, { method: 'POST' })
};

// Status and monitoring API
export const statusApi = {
  // Manually trigger a status check for an instance
  checkStatus: (instanceId: string): Promise<{ message: string }> => 
    fetchApi<{ message: string }>(`/test-status-update`, { 
      method: 'POST', 
      body: JSON.stringify({ instanceId }) 
    }),
  
  // Test if HAProxy service is running with status "offline"
  testOfflineStatus: (instanceId: string): Promise<{ message: string }> => 
    fetchApi<{ message: string }>(`/test-status-update`, { 
      method: 'POST', 
      body: JSON.stringify({ 
        instanceId, 
        status: 'offline' 
      }) 
    }),
  
  // Test if HAProxy API is unreachable but service is running
  testDegradedStatus: (instanceId: string): Promise<{ message: string }> => 
    fetchApi<{ message: string }>(`/test-status-update`, { 
      method: 'POST', 
      body: JSON.stringify({ 
        instanceId, 
        status: 'degraded' 
      }) 
    }),
  
  // Test if HAProxy is fully online
  testOnlineStatus: (instanceId: string): Promise<{ message: string }> => 
    fetchApi<{ message: string }>(`/test-status-update`, { 
      method: 'POST', 
      body: JSON.stringify({ 
        instanceId, 
        status: 'online' 
      }) 
    }),
    
  // Broadcast a custom status event
  broadcastStatus: (event: string, data: any): Promise<{ message: string }> => 
    fetchApi<{ message: string }>(`/broadcast`, { 
      method: 'POST', 
      body: JSON.stringify({ event, data }) 
    })
};

// Backends API
export const backendsApi = {
  getAll: (): Promise<HAProxyBackend[]> => 
    fetchApi<HAProxyBackend[]>('/backends'),
  
  getById: (id: string): Promise<HAProxyBackend> => 
    fetchApi<HAProxyBackend>(`/backends/${id}`),
  
  create: (backend: Omit<HAProxyBackend, 'id' | 'serverCount'> & { servers: HAProxyServer[] }): Promise<HAProxyBackend> => 
    fetchApi<HAProxyBackend>('/backends', { 
      method: 'POST', 
      body: JSON.stringify(backend) 
    }),
  
  update: (id: string, backend: Omit<HAProxyBackend, 'id' | 'serverCount'> & { servers: HAProxyServer[] }): Promise<HAProxyBackend> => 
    fetchApi<HAProxyBackend>(`/backends/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(backend) 
    }),
  
  delete: (id: string): Promise<void> => 
    fetchApi<void>(`/backends/${id}`, { method: 'DELETE' })
};

// Domain Mappings API
export const domainMappingsApi = {
  getAll: (): Promise<DomainMapping[]> => 
    fetchApi<DomainMapping[]>('/domain-mappings'),
  
  getByInstance: (instanceId: string): Promise<DomainMapping[]> => 
    fetchApi<DomainMapping[]>(`/domain-mappings/instance/${instanceId}`),
  
  getById: (id: string): Promise<DomainMapping> => 
    fetchApi<DomainMapping>(`/domain-mappings/${id}`),
  
  create: (mapping: Omit<DomainMapping, 'id'>): Promise<DomainMapping> => 
    fetchApi<DomainMapping>('/domain-mappings', { 
      method: 'POST', 
      body: JSON.stringify(mapping) 
    }),
  
  update: (id: string, mapping: Omit<DomainMapping, 'id'>): Promise<DomainMapping> => 
    fetchApi<DomainMapping>(`/domain-mappings/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(mapping) 
    }),
  
  toggleStatus: (id: string, active: boolean): Promise<DomainMapping> => 
    fetchApi<DomainMapping>(`/domain-mappings/${id}/toggle-status`, { 
      method: 'PATCH', 
      body: JSON.stringify({ active }) 
    }),
  
  delete: (id: string): Promise<void> => 
    fetchApi<void>(`/domain-mappings/${id}`, { method: 'DELETE' }),

  reorder: (priorities: {id: string, priority: number}[]): Promise<void> =>
    fetchApi<void>(`/domain-mappings/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ priorities })
    }),
};

// Templates API
export const templatesApi = {
  getAll: (): Promise<HAProxyTemplate[]> => 
    fetchApi<HAProxyTemplate[]>('/templates'),
  
  getById: (id: string): Promise<HAProxyTemplate> => 
    fetchApi<HAProxyTemplate>(`/templates/${id}`),
  
  getDefault: (): Promise<HAProxyTemplate> => 
    fetchApi<HAProxyTemplate>('/templates/default'),
  
  create: (template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>): Promise<HAProxyTemplate> => 
    fetchApi<HAProxyTemplate>('/templates', { 
      method: 'POST', 
      body: JSON.stringify(template) 
    }),
  
  update: (id: string, template: Omit<HAProxyTemplate, 'id' | 'createdAt' | 'updatedAt' | 'lastUsedAt'>): Promise<HAProxyTemplate> => 
    fetchApi<HAProxyTemplate>(`/templates/${id}`, { 
      method: 'PUT', 
      body: JSON.stringify(template) 
    }),
  
  setDefault: (id: string): Promise<HAProxyTemplate> => 
    fetchApi<HAProxyTemplate>(`/templates/${id}/default`, { method: 'PATCH' }),
  
  delete: (id: string): Promise<void> => 
    fetchApi<void>(`/templates/${id}`, { method: 'DELETE' }),
    
  preview: (templateId: string): Promise<{ content: string }> => 
    fetch(`/api/config?templateId=${templateId}`)
      .then(res => res.text())
      .then(content => ({ content }))
};

export const configApi = {
  deploy: (templateId: string): Promise<{ deploymentId: string }> => 
    fetchApi<{ deploymentId: string }>('/config/deploy', { 
      method: 'POST', 
      body: JSON.stringify({ templateId }) 
    })
};

// NFS API
export const nfsApi = {
  // NFS Servers
  getAllServers: (): Promise<NFSServer[]> => fetchApi<NFSServer[]>('/nfs/servers'),
  getServerById: (id: string): Promise<NFSServer> => fetchApi<NFSServer>(`/nfs/servers/${id}`),
  createServer: (server: Omit<NFSServer, 'id'>): Promise<NFSServer> => fetchApi<NFSServer>('/nfs/servers', { method: 'POST', body: JSON.stringify(server) }),
  updateServer: (id: string, server: Omit<NFSServer, 'id'>): Promise<NFSServer> => fetchApi<NFSServer>(`/nfs/servers/${id}`, { method: 'PUT', body: JSON.stringify(server) }),
  deleteServer: (id: string): Promise<void> => fetchApi<void>(`/nfs/servers/${id}`, { method: 'DELETE' }),

  // NFS Shares
  getAllShares: (): Promise<NFSShare[]> => fetchApi<NFSShare[]>('/nfs/shares'),
  getShareById: (id: string): Promise<NFSShare> => fetchApi<NFSShare>(`/nfs/shares/${id}`),
  createShare: (share: Omit<NFSShare, 'id'>): Promise<NFSShare> => fetchApi<NFSShare>('/nfs/shares', { method: 'POST', body: JSON.stringify(share) }),
  updateShare: (id: string, share: Omit<NFSShare, 'id'>): Promise<NFSShare> => fetchApi<NFSShare>(`/nfs/shares/${id}`, { method: 'PUT', body: JSON.stringify(share) }),
  deleteShare: (id: string): Promise<void> => fetchApi<void>(`/nfs/shares/${id}`, { method: 'DELETE' }),

  // Proxy to NFS API on instance
  getConfig: (serverId: string): Promise<any> => fetchApi<any>(`/nfs/config/${serverId}`),
  updateConfig: (serverId: string, config: any): Promise<any> => fetchApi<any>(`/nfs/config/${serverId}`, { method: 'PUT', body: JSON.stringify(config) }),
  deploy: (): Promise<any> => fetchApi<any>(`/nfs/deploy`, { method: 'POST' }),
  getStatus: (serverId: string): Promise<any> => fetchApi<any>(`/nfs/status/${serverId}`),
  reload: (serverId: string): Promise<any> => fetchApi<any>(`/nfs/reload/${serverId}`, { method: 'POST' }),
  getStats: (serverId: string): Promise<any> => fetchApi<any>(`/nfs/stats/${serverId}`),
};

export default {
  instances: instancesApi,
  backends: backendsApi,
  domainMappings: domainMappingsApi,
  templates: templatesApi,
  status: statusApi,
  config: configApi,
  nfs: nfsApi,
}; 